'use client';

import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { isAndroid } from '@/utils/browser';
import type { AnimationItem } from 'lottie-web';

interface IplFooterAnimateProps {
  /** 是否允许播放动画 */
  visible?: boolean;
  /** 根节点样式 */
  className?: string;
  /** 动画资源是否就绪 */
  onReadyChange?: (ready: boolean) => void;
}

interface NavigatorConnectionInfo {
  saveData?: boolean;
  effectiveType?: string;
  addEventListener?: (type: 'change', listener: () => void) => void;
  removeEventListener?: (type: 'change', listener: () => void) => void;
}

type NavigatorWithCapabilities = Navigator & {
  connection?: NavigatorConnectionInfo;
  deviceMemory?: number;
};

export default function IplFooterAnimate({
  visible = true,
  className,
  onReadyChange,
}: IplFooterAnimateProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<AnimationItem | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [canPlayAnimation, setCanPlayAnimation] = useState(true);
  const [isPageVisible, setIsPageVisible] = useState(true);
  const [shouldLoadAssets, setShouldLoadAssets] = useState(false);

  useEffect(() => {
    onReadyChange?.(isReady);
  }, [isReady, onReadyChange]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const updateAnimationCapability = () => {
      const navigatorInfo = window.navigator as NavigatorWithCapabilities;
      const connection = navigatorInfo.connection;
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const effectiveType = connection?.effectiveType?.toLowerCase();
      const isSlowNetwork = effectiveType === 'slow-2g' || effectiveType === '2g';
      const isLowMemoryDevice =
        typeof navigatorInfo.deviceMemory === 'number' && navigatorInfo.deviceMemory <= 2;
      const isLowCpuDevice =
        typeof navigatorInfo.hardwareConcurrency === 'number' &&
        navigatorInfo.hardwareConcurrency > 0 &&
        navigatorInfo.hardwareConcurrency <= 4;
      const isConstrainedAndroid3G =
        isAndroid() &&
        effectiveType === '3g' &&
        (isLowMemoryDevice || isLowCpuDevice);

      setCanPlayAnimation(
        !prefersReducedMotion &&
        !connection?.saveData &&
        !isSlowNetwork &&
        !isLowMemoryDevice &&
        !isConstrainedAndroid3G
      );
    };

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const navigatorInfo = window.navigator as NavigatorWithCapabilities;
    const connection = navigatorInfo.connection;

    updateAnimationCapability();

    const handleVisibilityChange = () => {
      setIsPageVisible(!document.hidden);
    };

    const handleMediaChange = () => {
      updateAnimationCapability();
    };

    setIsPageVisible(!document.hidden);

    document.addEventListener('visibilitychange', handleVisibilityChange);
    mediaQuery.addEventListener?.('change', handleMediaChange);
    mediaQuery.addListener?.(handleMediaChange);
    connection?.addEventListener?.('change', updateAnimationCapability);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      mediaQuery.removeEventListener?.('change', handleMediaChange);
      mediaQuery.removeListener?.(handleMediaChange);
      connection?.removeEventListener?.('change', updateAnimationCapability);
    };
  }, []);

  useEffect(() => {
    if (!canPlayAnimation || !visible || !isPageVisible) {
      setShouldLoadAssets(false);
      setIsReady(false);
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let idleId: number | null = null;

    const loadWhenIdle = () => {
      setShouldLoadAssets(true);
    };

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(loadWhenIdle, { timeout: 1200 });
    } else {
      timeoutId = setTimeout(loadWhenIdle, 180);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      if (
        idleId !== null &&
        typeof window !== 'undefined' &&
        'cancelIdleCallback' in window
      ) {
        window.cancelIdleCallback(idleId);
      }
    };
  }, [canPlayAnimation, isPageVisible, visible]);

  useEffect(() => {
    if (!canPlayAnimation || !shouldLoadAssets || !containerRef.current) {
      setIsReady(false);
      return;
    }

    let cancelled = false;
    let frameId: number | null = null;
    let nextFrameId: number | null = null;
    let localInstance: AnimationItem | null = null;
    let handleReady: (() => void) | null = null;
    let handleDataFailed: (() => void) | null = null;

    const loadAnimation = async () => {
      try {
        const [{ default: lottie }, { default: animationData }] = await Promise.all([
          import('lottie-web'),
          import('./ipl-icon.json'),
        ]);

        if (cancelled || !containerRef.current) {
          return;
        }

        localInstance = lottie.loadAnimation({
          container: containerRef.current,
          renderer: 'canvas',
          loop: true,
          autoplay: false,
          animationData,
          rendererSettings: {
            preserveAspectRatio: 'xMidYMid meet',
            clearCanvas: true,
          },
        });

        handleReady = () => {
          localInstance?.goToAndStop(0, true);
          frameId = window.requestAnimationFrame(() => {
            nextFrameId = window.requestAnimationFrame(() => {
              if (!cancelled) {
                setIsReady(true);
              }
            });
          });
        };

        handleDataFailed = () => {
          if (!cancelled) {
            setIsReady(false);
          }
        };

        animationRef.current = localInstance;
        localInstance.addEventListener('DOMLoaded', handleReady);
        localInstance.addEventListener('data_ready', handleReady);
        localInstance.addEventListener('loaded_images', handleReady);
        localInstance.addEventListener('data_failed', handleDataFailed);
      } catch {
        if (!cancelled) {
          setIsReady(false);
        }
      }
    };

    loadAnimation();

    return () => {
      cancelled = true;

      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }

      if (nextFrameId !== null) {
        window.cancelAnimationFrame(nextFrameId);
      }

      if (localInstance && handleReady) {
        localInstance.removeEventListener('DOMLoaded', handleReady);
        localInstance.removeEventListener('data_ready', handleReady);
        localInstance.removeEventListener('loaded_images', handleReady);
      }

      if (localInstance && handleDataFailed) {
        localInstance.removeEventListener('data_failed', handleDataFailed);
      }

      localInstance?.destroy();
      animationRef.current = null;
      setIsReady(false);
    };
  }, [canPlayAnimation, shouldLoadAssets]);

  useEffect(() => {
    const instance = animationRef.current;
    if (!instance) return;

    if (visible && canPlayAnimation && isPageVisible && isReady) {
      instance.play();
      return;
    }

    instance.pause();
  }, [canPlayAnimation, isPageVisible, isReady, visible]);

  if (!canPlayAnimation) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className={clsx(
        'pointer-events-none absolute left-1/2 top-1/2 h-[82px] w-[82px] max-w-none -translate-x-1/2 -translate-y-[calc(50%+13px)]',
        !isReady && 'opacity-0',
        className
      )}
      aria-hidden="true"
    />
  );
}
